// frontend/src/components/UsageWidget.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { getUsage } from "../api/orgs";
import PlanBadge from "./PlanBadge";
import { planMeta, metricNumber } from "../lib/planMeta";

/** Threshold (%) at which a bar turns amber / red. */
const WARN_AT = 80;
const DANGER_AT = 95;

/** Pick the bar colour from its fill percentage. */
function barColor(pct) {
  if (pct >= DANGER_AT) return "bg-red-500";
  if (pct >= WARN_AT) return "bg-yellow-400";
  return "bg-brand-500";
}

/**
 * Build a quantified upgrade nudge for the metric closest to its cap.
 * Reads the current and next-tier limits from pricing.js (via planMeta) so the
 * number always matches the public pricing page (PRIVATE-AREA-PLAN PA1-T5).
 *
 * @returns {?string} e.g. "Pro gives you 50,000 scans/month — 10× more." or null.
 */
function upgradeNudge(planName, topBar) {
  const { id, next } = planMeta(planName);
  if (!next || !topBar) return null;

  // Map the usage bar to its pricing.js comparison row + a human noun.
  const MAP = {
    "QR Codes Used": { label: "Active QR codes", noun: "active codes" },
    "Scans This Month": { label: "Scans per month", noun: "scans/month" },
  };
  const m = MAP[topBar.label];
  if (!m) return `Upgrade to ${next.name} for higher limits.`;

  const cur = metricNumber(id, m.label);
  const nextVal = metricNumber(next.id, m.label);
  if (!Number.isFinite(nextVal) || nextVal <= 0) {
    return `Upgrade to ${next.name} for higher limits.`;
  }
  const mult = Number.isFinite(cur) && cur > 0 ? Math.round(nextVal / cur) : 0;
  const more = mult >= 2 ? ` — ${mult}× more` : "";
  return `${next.name} gives you ${nextVal.toLocaleString()} ${m.noun}${more}.`;
}

/**
 * One labelled usage bar.
 *
 * @param {string} label   Row label, e.g. "QR Codes Used".
 * @param {number} used    Current usage count.
 * @param {?number} limit  Numeric cap, or null when the feature has no cap.
 * @param {number} pct     Usage percentage (0 when no numeric limit applies).
 */
function UsageBar({ label, used, limit, pct }) {
  const width = Math.min(pct, 100);
  const denom = limit == null ? "—" : limit.toLocaleString();
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500">
          {used.toLocaleString()} / {denom}
        </span>
      </div>
      <div
        className="h-2 w-full rounded-full bg-gray-100 overflow-hidden"
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-full transition-all ${barColor(pct)}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

/**
 * UsageWidget — plan badge + current-month usage bars for the dashboard.
 *
 * Fetches ``GET /api/orgs/me/usage`` on mount and renders three progress bars
 * (QR Codes, Scans, API Calls). Bars turn amber at 80% and red at 95%. When any
 * metric is over 80%, an "Upgrade Plan" link to /billing appears.
 *
 * Self-contained and resilient: a fetch failure renders a compact inline
 * message rather than breaking the surrounding dashboard.
 */
function UsageWidget({ usage: initialUsage }) {
  const [data, setData] = useState(initialUsage || null);
  const [loading, setLoading] = useState(!initialUsage);
  const [error, setError] = useState(false);

  useEffect(() => {
    // When the parent supplies the payload (the dashboard fetches it once and
    // shares it with the alerts panel), skip our own request entirely.
    if (initialUsage) {
      setData(initialUsage);
      setLoading(false);
      return undefined;
    }
    let active = true;
    getUsage()
      .then((d) => active && setData(d))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [initialUsage]);

  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading usage"
        className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse"
      >
        <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-2 w-full bg-gray-100 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-500">
        Usage data is unavailable right now.
      </div>
    );
  }

  const { plan, usage, percentages, limits } = data;
  const apiLimit =
    typeof limits?.max_api_calls === "number" ? limits.max_api_calls : null;

  const bars = [
    {
      label: "QR Codes Used",
      used: usage.codes_created,
      limit: limits?.max_codes ?? null,
      pct: percentages.codes_pct,
    },
    {
      label: "Scans This Month",
      used: usage.scans_recorded,
      limit: limits?.max_scans_per_month ?? null,
      pct: percentages.scans_pct,
    },
    {
      label: "API Calls This Month",
      used: usage.api_calls,
      limit: apiLimit,
      pct: percentages.api_calls_pct,
    },
  ];

  // The bar nearest its cap (and over the warn threshold) drives the nudge copy.
  const topBar = bars
    .filter((b) => b.pct >= WARN_AT)
    .sort((a, b) => b.pct - a.pct)[0];
  const nudge = topBar ? upgradeNudge(plan.name, topBar) : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Plan Usage</h2>
        <PlanBadge usage={data} />
      </div>

      <div className="space-y-3">
        {bars.map((b) => (
          <UsageBar
            key={b.label}
            label={b.label}
            used={b.used}
            limit={b.limit}
            pct={b.pct}
          />
        ))}
      </div>

      {topBar && (
        <div className="rounded-lg bg-brand-50 p-3">
          {nudge && (
            <p className="text-xs font-medium text-brand-900">{nudge}</p>
          )}
          <Link
            to="/billing"
            className="mt-1 inline-block text-sm font-semibold text-brand-700 hover:text-brand-800"
          >
            Upgrade plan →
          </Link>
        </div>
      )}
    </div>
  );
}

UsageWidget.propTypes = {
  /** Pre-fetched GET /api/orgs/me/usage payload; skips the internal fetch. */
  usage: PropTypes.object,
};
UsageWidget.defaultProps = { usage: null };

export default UsageWidget;
